'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { usePathname, useSearchParams } from 'next/navigation'
import { BaseSyntheticEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useCheckAuth } from '@/features/auth/check'

import { useAssent } from '@/entities/assent'
import { useGetUserDataQuery, useGetUserSubscriptionQuery, useUser } from '@/entities/user'

import { getPhoneFromJwtToken } from '@/shared/lib/utils/jwt/get-phone-from-jwt-token'
import { normalizeEmail } from '@/shared/lib/validation/email'
import { normalizePhone } from '@/shared/lib/validation/phone'

import { usePostBonusConversionFormMutation } from '../../../api/bonus-conversion-api'
import {
	BONUS_CONVERSION_AUTO_FLOW_TIMEOUT_MS,
	BONUS_CONVERSION_PHONE_ERROR_TEXT,
	BONUS_CONVERSION_REQUEST_ERROR_TEXT
} from '../../../model/constants'
import { getPageUrl } from '../../../utils/get-page-url'
import { bonusConversionFormSchema, BonusConversionFormValues } from '../model/bonus-conversion-form.schema'

type UseBonusConversionFormResult = {
	control: ReturnType<typeof useForm<BonusConversionFormValues>>['control']
	errors: ReturnType<typeof useForm<BonusConversionFormValues>>['formState']['errors']
	userInfoIsLoading: boolean
	showNewsletterCheckbox: boolean
	isNewsletterAccepted: boolean
	setIsNewsletterAccepted: (checked: boolean) => void
	submitHandler: (event?: BaseSyntheticEvent) => Promise<void>
	onManualSubmitClick: () => void
	isLoading: boolean
	canSubmit: boolean
	hasPhone: boolean
	errorMessage: string
	isSuccess: boolean
	isAutoFlowResolved: boolean
}

export const useBonusConversionForm = (): UseBonusConversionFormResult => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const { user: userFromCookie, isLoading: userFromCookieIsLoading } = useUser()
	const { authToken } = useCheckAuth()
	const {
		data: userByTokenResponse,
		isLoading: userByTokenIsLoading,
		isFetching: userByTokenIsFetching
	} = useGetUserDataQuery(undefined, {
		skip: !authToken || !!userFromCookie,
		refetchOnMountOrArgChange: true
	})
	const [postBonusConversionForm, { isLoading }] = usePostBonusConversionFormMutation()

	const [isSuccess, setIsSuccess] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [isAutoFlowResolved, setIsAutoFlowResolved] = useState(false)
	const [isAutoSubmitting, setIsAutoSubmitting] = useState(false)
	const [isAutoFlowTimeoutReached, setIsAutoFlowTimeoutReached] = useState(false)
	const submitLockRef = useRef(false)

	const {
		showNewsletterCheckbox,
		isAccepted: isNewsletterAccepted,
		setIsAccepted: setIsNewsletterAccepted,
		checkConsent,
		saveConsent,
		hasConsent
	} = useAssent()

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors, isDirty }
	} = useForm<BonusConversionFormValues>({
		mode: 'onBlur',
		resolver: zodResolver(bonusConversionFormSchema),
		defaultValues: {
			NAME: '',
			EMAIL: ''
		}
	})

	const user = userFromCookie || userByTokenResponse?.data
	const userInfoIsLoading = userFromCookie ? userFromCookieIsLoading : userByTokenIsLoading || userByTokenIsFetching

	const normalizedUserPhoneFromToken = useMemo(() => {
		const tokenPhone = getPhoneFromJwtToken(authToken)
		const tokenPhoneAsString =
			typeof tokenPhone === 'string'
				? tokenPhone
				: tokenPhone !== null && tokenPhone !== undefined
				? String(tokenPhone)
				: ''

		return normalizePhone(tokenPhoneAsString)
	}, [authToken])
	const normalizedUserPhone = useMemo(
		() => normalizePhone(user?.user_phone) || normalizedUserPhoneFromToken,
		[user?.user_phone, normalizedUserPhoneFromToken]
	)
	const normalizedUserName = (user?.first_name || '').trim()
	const normalizedUserEmail = user?.user_email && user.user_email !== '0' ? normalizeEmail(user.user_email) : ''
	const { data: userSubscriptionData, isLoading: userSubscriptionIsLoading } = useGetUserSubscriptionQuery(undefined, {
		skip: !user
	})

	const isSubscribedFromUserProfile = useMemo(() => {
		const subscriptions = userSubscriptionData?.data
		if (!subscriptions?.length) return false

		return subscriptions.some(item => item?.pointOfContact?.toLowerCase() === 'email' && item?.isSubscribed)
	}, [userSubscriptionData])

	const hasValidName = bonusConversionFormSchema.shape.NAME.safeParse(normalizedUserName).success
	const hasValidEmail = bonusConversionFormSchema.shape.EMAIL.safeParse(normalizedUserEmail).success
	const hasPhone = !!normalizedUserPhone
	const canSubmit = hasPhone && !userInfoIsLoading

	useEffect(() => {
		const timeout = setTimeout(() => {
			setIsAutoFlowTimeoutReached(true)
		}, BONUS_CONVERSION_AUTO_FLOW_TIMEOUT_MS)

		return () => clearTimeout(timeout)
	}, [])

	useEffect(() => {
		if (!normalizedUserPhone) return
		checkConsent(normalizedUserPhone)
	}, [checkConsent, normalizedUserPhone])

	useEffect(() => {
		if (!user) return

		setValue('NAME', user.first_name)

		if (user.user_email && user.user_email !== '0') {
			setValue('EMAIL', normalizeEmail(user.user_email))
		}
	}, [setValue, user])

	const submitForm = useCallback(
		async (values: BonusConversionFormValues) => {
			if (submitLockRef.current) return
			submitLockRef.current = true

			setErrorMessage('')
			if (!normalizedUserPhone) {
				setErrorMessage(BONUS_CONVERSION_PHONE_ERROR_TEXT)
				submitLockRef.current = false
				return
			}

			try {
				await postBonusConversionForm({
					formData: {
						NAME: values.NAME,
						EMAIL: values.EMAIL,
						PHONE: normalizedUserPhone
					},
					url: getPageUrl(`${pathname || '/'}${searchParams?.toString() ? `?${searchParams?.toString()}` : ''}`)
				}).unwrap()

				if (!hasConsent && isNewsletterAccepted) {
					await saveConsent(normalizedUserPhone)
				}

				setIsSuccess(true)
			} catch {
				setErrorMessage(BONUS_CONVERSION_REQUEST_ERROR_TEXT)
			} finally {
				submitLockRef.current = false
			}
		},
		[
			hasConsent,
			isNewsletterAccepted,
			normalizedUserPhone,
			postBonusConversionForm,
			pathname,
			searchParams,
			saveConsent
		]
	)

	useEffect(() => {
		if (isSuccess || isLoading || isAutoSubmitting) return

		const canStillWaitForData =
			!isAutoFlowTimeoutReached && (userInfoIsLoading || (user && userSubscriptionIsLoading) || hasConsent === null)
		if (canStillWaitForData) return

		const hasSubscriptionConsent = isSubscribedFromUserProfile || hasConsent === true
		const shouldAutoSubmit = hasValidName && hasValidEmail && hasSubscriptionConsent

		if (shouldAutoSubmit) {
			// Если форма уже показана пользователю и он начал редактировать поля —
			// не запускаем поздний автосабмит, чтобы не перехватить ручной сценарий.
			if (isAutoFlowResolved && isDirty) return

			const send = async () => {
				setIsAutoSubmitting(true)
				await submitForm({
					NAME: normalizedUserName,
					EMAIL: normalizedUserEmail
				})
				setIsAutoSubmitting(false)
				setIsAutoFlowResolved(true)
			}

			void send()
		} else {
			if (isAutoFlowResolved) return
			setIsAutoFlowResolved(true)
		}
	}, [
		hasConsent,
		hasValidEmail,
		hasValidName,
		isDirty,
		isSubscribedFromUserProfile,
		isAutoFlowResolved,
		isAutoSubmitting,
		isAutoFlowTimeoutReached,
		isLoading,
		isSuccess,
		normalizedUserEmail,
		normalizedUserName,
		submitForm,
		user,
		userInfoIsLoading,
		userSubscriptionIsLoading
	])

	const onSubmit = useCallback(
		async (values: BonusConversionFormValues) => {
			await submitForm(values)
		},
		[submitForm]
	)

	const submitHandler = handleSubmit(onSubmit)
	const onManualSubmitClick = () => {
		void submitHandler()
	}

	return {
		control,
		errors,
		userInfoIsLoading,
		showNewsletterCheckbox,
		isNewsletterAccepted,
		setIsNewsletterAccepted,
		submitHandler,
		onManualSubmitClick,
		isLoading,
		canSubmit,
		hasPhone,
		errorMessage,
		isSuccess,
		isAutoFlowResolved
	}
}
